import React from 'react';
import { MapPin } from 'lucide-react';
import { LocationOverviewBinDto, LocationOverviewTotalsDto } from '../../../../../types/locationOverview';
import { useTranslation } from '../../../../../contexts/I18nProvider';
import { formatDate, formatQty } from '../../item-card/format';

interface LocationHeaderProps {
  bin: LocationOverviewBinDto;
  totals: LocationOverviewTotalsDto;
}

type KpiTone = 'neutral' | 'orange' | 'green' | 'red';

const KPI_TONES: Record<KpiTone, { bg: string; text: string }> = {
  neutral: { bg: 'bg-gray-50', text: 'text-gray-900' },
  orange: { bg: 'bg-orange-50', text: 'text-orange-600' },
  green: { bg: 'bg-green-50', text: 'text-green-600' },
  red: { bg: 'bg-red-50', text: 'text-red-700' },
};

const Kpi: React.FC<{ label: string; value: number; tone: KpiTone }> = ({ label, value, tone }) => (
  <div className={`${KPI_TONES[tone].bg} p-3 rounded-lg text-center`}>
    <div className="text-xs text-gray-500 mb-1">{label}</div>
    <div className={`text-lg font-semibold ${KPI_TONES[tone].text}`}>{formatQty(value)}</div>
  </div>
);

const Chip: React.FC<{ children: React.ReactNode; tone?: 'gray' | 'orange' | 'blue' }> = ({ children, tone = 'gray' }) => {
  const classes = {
    gray: 'bg-gray-100 text-gray-600',
    orange: 'bg-orange-100 text-orange-700',
    blue: 'bg-blue-100 text-blue-700',
  }[tone];
  return <span className={`inline-flex items-center text-xs font-medium px-2 py-0.5 rounded-full ${classes}`}>{children}</span>;
};

/**
 * Topp av lokasjonsoppslaget: lokasjon, lager/sone/gruppe, avvik fra normal lokasjon som merker
 * (ikke plukkbar, ikke reserverbar, felles, krever lokasjonsskann) og saldo-nøkkeltall.
 */
export const LocationHeader: React.FC<LocationHeaderProps> = ({ bin, totals }) => {
  const { t } = useTranslation();
  const prefix = 'warehouse.modules.locationLookup.header';
  const zone = bin.zoneDescription || bin.stcZoneID || '';

  return (
    <div className="bg-white rounded-lg shadow-sm p-4 sm:p-5">
      <div className="flex items-start gap-3 sm:gap-4">
        <div className="flex-shrink-0 bg-blue-100 rounded-full p-3">
          <MapPin className="h-7 w-7 sm:h-8 sm:w-8 text-blue-600" />
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 font-mono break-words">{bin.binCode}</h1>
          {bin.description1 && bin.description1 !== bin.binCode && (
            <p className="text-sm sm:text-base text-gray-600 break-words mt-0.5">{bin.description1}</p>
          )}
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <Chip>{t(`${prefix}.warehouse`, { '0': bin.warehouseID }, `Lager ${bin.warehouseID}`)}</Chip>
            {zone && <Chip>{t(`${prefix}.zone`, { '0': zone }, `Sone ${zone}`)}</Chip>}
            {bin.binGrpDescription && <Chip>{bin.binGrpDescription}</Chip>}
            {bin.pickableYesNo === false && <Chip tone="orange">{t(`${prefix}.notPickable`, {}, 'Ikke plukkbar')}</Chip>}
            {bin.reservationAllowed === false && <Chip tone="orange">{t(`${prefix}.notReservable`, {}, 'Ikke reserverbar')}</Chip>}
            {bin.sharedBinYesNo && <Chip tone="blue">{t(`${prefix}.shared`, {}, 'Felles lokasjon')}</Chip>}
            {bin.barCodeConfirmLocation === true && <Chip tone="blue">{t(`${prefix}.scanRequired`, {}, 'Krever lokasjonsskann')}</Chip>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mt-4">
        <Kpi label={t(`${prefix}.items`, {}, 'Varer')} value={totals.itemCount} tone="neutral" />
        <Kpi label={t(`${prefix}.physical`, {}, 'På lager')} value={totals.physical} tone="neutral" />
        <Kpi label={t(`${prefix}.reserved`, {}, 'Reservert')} value={totals.reserved} tone="orange" />
        <Kpi label={t(`${prefix}.available`, {}, 'Tilgjengelig')} value={totals.available} tone={totals.available < 0 ? 'red' : 'green'} />
      </div>

      <p className="mt-3 text-xs text-gray-500">
        {t(`${prefix}.lastCounted`, {}, 'Sist telt')}: {formatDate(bin.lastCountedDate)}
        {' · '}
        {t(`${prefix}.lastMovement`, {}, 'Sist bevegelse')}: {formatDate(bin.lastMovementDate)}
      </p>
    </div>
  );
};
